import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, BookOpen, User, Calendar } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

const categoryLabels = {
  upton_park: "Upton Park/Boleyn Ground",
  european: "European Nights",
  world_cup: "1966 World Cup",
  away_days: "Away Days",
  derby: "Derby Days",
  legends: "Club Legends",
  general: "General Memories",
};

export default function FanMemoryDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initial = readSsgInitialData(slug);
  const [memory, setMemory] = useState(initial && initial.title ? initial : null);
  const [loading, setLoading] = useState(!(initial && initial.title));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    const fresh = readSsgInitialData(slug);
    setMemory(fresh && fresh.title ? fresh : null);
    setLoading(!(fresh && fresh.title));
    setNotFound(false);
    axios
      .get(`${API}/fan-memories/by-slug/${slug}`)
      .then((res) => alive && (setMemory(res.data), setLoading(false)))
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading || (!memory && !notFound)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading..." path={`/fan-memories/${slug}`} />
        <div className="flex items-center justify-center py-40"><div className="spinner" /></div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="fan-memory-notfound">
        <Navbar />
        <SEO title="Memory not found" path={`/fan-memories/${slug}`} noindex={true} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            MEMORY NOT FOUND
          </h1>
          <Link to="/fan-memories" className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Fan Memories
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryLabel = categoryLabels[memory.category] || memory.category;
  const metaDesc = (memory.content || memory.title || "").slice(0, 160).replace(/\s+/g, " ").trim();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: memory.title,
    datePublished: memory.published_at || memory.created_at,
    dateModified: memory.published_at || memory.created_at,
    author: { "@type": "Person", name: memory.author_name || "West Ham Fan" },
    publisher: {
      "@type": "Organization",
      name: "All WHUFC",
      logo: { "@type": "ImageObject", url: `${BASE_URL}/og-image.png` },
    },
    image: memory.image_url ? [memory.image_url] : undefined,
    mainEntityOfPage: `${BASE_URL}/fan-memories/${memory.slug}`,
    description: metaDesc,
    articleSection: categoryLabel,
  };

  const dateFmt = memory.published_at || memory.created_at
    ? new Date(memory.published_at || memory.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fan-memory-detail-page">
      <SEO
        title={`${memory.title} — Fan Memory`}
        path={`/fan-memories/${memory.slug}`}
        description={metaDesc}
        type="article"
        image={memory.image_url}
        jsonLd={jsonLd}
        seoTitle={memory.seo_title}
        seoDescription={memory.seo_description}
        keywords={memory.seo_keywords}
        imageAlt={memory.image_alt}
      />
      <Navbar />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#7C2C3B] mb-6"
          data-testid="fan-memory-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-xs font-semibold text-[#7C2C3B] uppercase tracking-wide mb-2">{categoryLabel}</div>
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] leading-tight mb-3" style={{ fontFamily: "Barlow Condensed" }}>
          {memory.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
          <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {memory.author_name || "West Ham Fan"}</span>
          {dateFmt && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateFmt}</span>}
        </div>

        {memory.image_url && (
          <img
            src={memory.image_url}
            alt={memory.title}
            className="w-full rounded-lg mb-6 object-cover max-h-[480px]"
          />
        )}

        <div className="prose prose-slate max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed">
          {memory.content}
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200">
          <SocialShareButtons shareText={memory.title} />
        </div>
      </article>

      <Footer />
    </div>
  );
}
